import React from 'react';
import { createPortal } from 'react-dom';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { ChipButton } from './ChipButton';

type Action = () => void;

export interface SwipeActionControlsProps {
  onHit: Action;
  onStand: Action;
  onDouble: Action;
  onSplit: Action;
  onSurrender?: Action;
  canDouble: boolean;
  canSplit: boolean;
  canSurrender?: boolean;
  /**
   * Whether to render anything at all. If false/null, returns null.
   */
  show?: boolean;
  /**
   * Render into document.body to avoid clipping by transforms.
   */
  usePortal?: boolean;
  /**
   * Swipe distance threshold in px.
   */
  swipeThreshold?: number;
  /**
   * Extra classes for the fixed wrapper.
   */
  wrapperClassName?: string;
}

export const SwipeActionControls: React.FC<SwipeActionControlsProps> = ({
  onHit,
  onStand,
  onDouble,
  onSplit,
  onSurrender,
  canDouble,
  canSplit,
  canSurrender,
  show = true,
  usePortal = false,
  swipeThreshold = 60,
  wrapperClassName = ''
}) => {
  const { 
    onTouchStart, 
    onTouchMove, 
    onTouchEnd, 
    isSwiping, 
    swipeDirection, 
    swipeProgress 
  } = useSwipeGesture({
    onSwipeLeft: onStand,
    onSwipeRight: onHit,
    onSwipeUp: canDouble ? onDouble : undefined,
    onSwipeDown: canSplit ? onSplit : undefined
  }, swipeThreshold);

  if (!show) return null;

  const controls = (
    <div 
      className={`fixed bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/95 to-transparent md:hidden z-50 ${wrapperClassName}`}
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="px-4 pb-4 pt-2">
        <div className="text-center mb-2 text-gray-200 text-xs">
          ← Stand | Hit → {canDouble ? '| ↑ Double' : ''} {canSplit ? '| ↓ Split' : ''}
        </div>
        <div
          className="relative bg-gray-800/50 rounded-xl p-4 border border-gray-700 touch-pan-x touch-pan-y mb-2"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-white">
            {isSwiping && swipeDirection && (
              <div className={`text-3xl font-bold transition-opacity ${
                swipeProgress > 0.5 ? 'opacity-100' : 'opacity-50'
              }`}>
                {swipeDirection === 'right' && 'HIT'}
                {swipeDirection === 'left' && 'STAND'}
                {swipeDirection === 'up' && canDouble && 'DOUBLE'}
                {swipeDirection === 'down' && canSplit && 'SPLIT'}
              </div>
            )}
            {!isSwiping && (
              <div className="text-gray-200 text-base font-semibold">
                Swipe to Play
              </div>
            )}
          </div>
          {isSwiping && swipeDirection && (
            <div className="absolute bottom-1 left-2 right-2">
              <div className="bg-gray-700 h-1 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    swipeProgress > 0.8 
                      ? 'bg-green-500' 
                      : 'bg-blue-500'
                  }`}
                  style={{ width: `${swipeProgress * 100}%` }}
                />
              </div>
            </div>
          )}
          <div className="h-16" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ChipButton label="Hit" onClick={onHit} />
          <ChipButton label="Stand" onClick={onStand} />
          <ChipButton label="Double" onClick={onDouble} disabled={!canDouble} />
          {canSurrender ? (
            <ChipButton label="Surrender" onClick={onSurrender} disabled={!canSurrender} />
          ) : (
            <div />
          )}
          <ChipButton label="Split" onClick={onSplit} disabled={!canSplit} className="col-span-2" />
        </div>
      </div>
    </div>
  );

  if (usePortal && typeof document !== 'undefined') {
    return createPortal(controls, document.body);
  }

  return controls;
};

